import type { IntlShape } from 'react-intl-next';

import type { MediaOptions } from '@atlaskit/editor-plugin-media/types';
import type { LinkPickerPlugin } from '@atlaskit/link-picker';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
// import { createPromptEditor } from '@atlassian/editor-ai-injected-editors/prompt-editor';
// import { createPageEditorPluginAIOptions } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import { hubPreset, type HubPresetBuilder } from '@atlassian/hub-presets/hub';

import type { HubEditorProps } from '../../types';

type LinkPickerOptions = {
	linkPickerPlugins: LinkPickerPlugin[];
};

export interface CreateHubEditorPresetProps extends HubEditorProps {
	intl: IntlShape;
	linkPickerOptions?: LinkPickerOptions;
	mediaOptions?: MediaOptions;
	featureFlags?: {
		[key: string]: boolean;
	}
}

export function createHubEditorPreset(props: CreateHubEditorPresetProps): HubPresetBuilder {

	const { intl, mediaOptions, linkPickerOptions, featureFlags } = props;
	const __livePage = false;

	const isCSM = props.product === 'CSM';

	// const editorPluginAIOptions = createPageEditorPluginAIOptions({
	// 	product,
	// 	aiGlobalOptIn: { status: 'disabled', triggerOptInFlow: () => { } },
	// 	PromptEditor: createPromptEditor({
	// 		enableLinks: false,
	// 		linking: {
	// 			smartLinks: { provider: undefined },
	// 		},
	// 		mentionProvider: undefined,
	// 		featureFlags: {},
	// 	}),
	// 	isRovoEnabled: false,
	// });

	return hubPreset({
		intl,
		pluginOptions: {
			base: {
				__livePage,
			},
			helpDialog: {
				imageUploadProviderExists: Boolean(mediaOptions?.provider),
				aiEnabled: false,
			},
			hyperlink: {
				linkPicker: {
					plugins: linkPickerOptions?.linkPickerPlugins || [],
				},
				onClickCallback: undefined,
			},
			quickInsert: {
				emptyStateHandler: undefined,
				quickInsert: props.quickInsert,
			},
			placeholder: {
				viewMode: props.viewMode,
				isAIEnabled: false,
			},
			selection: {
				__livePage,
			},
			editorDisabled: {
				disabled: undefined,
			},
			annotation: {
				annotationManager: undefined,
				createCommentExperience: undefined,
				selectCommentExperience: undefined,
				viewInlineCommentTraceUFOPress: undefined,
			},
			tasksAndDecisions: {
				hasEditPermission: props.hasEditPermission,
				requestToEditContent: undefined,
				hasRequestedEditPermission: undefined,
			},
			contextPanel: props.contextPanelOptions
				? {
						objectSideBar: props.contextPanelOptions.objectSideBar,
					}
				: undefined,
			extension: {
				extensionHandlers: props.extensionHandlers,
				__rendererExtensionOptions: props.__rendererExtensionOptions,
				getExtensionHeight: props.getExtensionHeight,
			},
			card: {
				__livePage,
				linkPicker: props.linking?.linkPicker,
				onClickCallback: props.linking?.smartLinks?.onClickCallback,
			},
			avatarGroup: {
				collabEdit: undefined,
			},
			// ai: editorPluginAIOptions,
			loom: {
				renderButton: () => null,
			},
			editorViewMode: {
				viewMode: props.viewMode,
			},
			// aiDefinitions: props.editorPluginAIDefinitionsConfig,
			selectionMarker: {
				__livePage,
			},
			engagementPlatform: {
				coordinationClient: {
					start: () => Promise.resolve(false),
					stop: () => Promise.resolve(false),
				},
			},
			selectionExtension: {},
			metrics: {},
			primaryToolbar: {
				contextualFormattingEnabled: true,
			},
			selectionToolbar: {
				contextualFormattingEnabled: true,
			},
		},
		providers: {
			userPreferencesProvider: props.userPreferencesProvider,
			userPreferencesProviderNext: props.userPreferencesProviderNext,
			cardProvider: props.linking?.smartLinks?.provider,
			contextIdentifierProvider: props.contextIdentifierProvider,
			inlineCommentAnnotationProvider: props.annotationProviders?.inlineComment,
			mediaProvider: mediaOptions?.provider,
			syncMediaProvider: props.media?.syncProvider,
			mentionProvider: props.mentionProvider,
			profilecardProvider: props.mention?.profilecardProvider,
			emojiProvider: props.emojiProvider,
			autoformattingProvider: props.autoformattingProvider,
			taskDecisionProvider: props.taskDecisionProvider,
		},
		enabledOptionalPlugins: {
			toolbar: isCSM ? fg('hub_enable_inline_toolbar_plugin') : true,
			findReplace: Boolean(props.allowFindReplace),
			loom: props.isLoomPluginEnabled,
			aiDefinitions: props.editorPluginAIDefinitionsEnabled,
			connectivity:
				props.isDesktop || !expValEquals('platform_editor_offline_editing_web', 'isEnabled', true),
			metrics: fg('platform_editor_ease_of_use_metrics'),
			codeBlockAdvanced: true,
			selectionExtension: expValEquals('platform_editor_controls', 'cohort', 'variant1'),
			// aiExperience: Boolean(props.editorPluginAIOptions?.aiGlobalOptIn?.status !== 'disabled'),
			interaction: expValEquals('platform_editor_no_cursor_on_edit_page_init', 'isEnabled', true),
			userPreferences: fg('platform_editor_use_preferences_plugin'),
			companyHubTextColor: props.editorPluginHubTextColorEnabled ?? false,
			// This plugin provides support for showing an indicator when we drag and drop components from components panel
			hubExternalInsert: featureFlags?.hub_editor_floating_toolbar ?? false,
		},
	});
}
